import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/authcontext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomNavbar from './components/CustomNavbar';
import GlobalIncomingCallHandler from './components/GlobalIncomingCallHandler';
import './style.css';

// Pages
import HomePage from './pages/home';
import PlansPage from './pages/plans';
import DoctorsPage from './pages/doctors';
import ServicesPage from './pages/services';
import SignupPage from './pages/signup';
import LoginPage from './pages/login';
import ForgotPasswordPage from './pages/ForgotPassword';
import DocLoginPage from './pages/doctorLogin';
import LiveChatPage from './pages/liveChat';
import ReportProblemPage from './pages/ReportProblem';
import ProfilePage from './pages/profileComponent';
import BookingPage from './pages/booking';
import DocHomePage from './pages/dochome';
import VideoChatPage from './pages/VideoChat';
import MyBookings from './pages/Mybookings';
import ResetPasswordPage from './pages/ResetPassword';
// ✅ NEW: replaces the disconnected mock liveChat.jsx — real conversations,
// tied to actual bookings, delivered over the same socket used for calls.
import Messages from './pages/Messages';
// ✅ NEW: matching quiz, scores against the real specialisation categories
import MatchingQuiz from './pages/MatchingQuiz';

// Graphologist pages
import GraphBookingPage from './pages/Graphbookingpage';
import GraphLogin from './pages/GraphLogin';
// ✅ NEW: GraphDashboard was missing — this was why /graph-dashboard showed 404
import GraphDashboard from './pages/Graphdashboard';
// ✅ NEW: the "Handwriting Analysis →" link on /my-bookings had nowhere to go
import MyGraphBookings from './pages/MyGraphBookings';
// ✅ NEW: internal-only, gated by its own secret prompt — not a client/doctor/graphologist route
import AdminDoctors from './pages/AdminDoctors';

// ✅ FIXED: added /graph-dashboard so navbar hides on the graphologist dashboard
const hideNavbarPaths = [
  '/login', '/signup', '/doctorLogin', '/graph-login',
  '/forgot-password', '/reset-password', '/graph-dashboard', '/admin'
];

const NavigationGuard = ({ children }) => children;

const AuthAwareRoute = ({ children, requiresAuth = false, roles = [], redirectIfAuth = false }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '10px' }}>
        <div className="spinner" style={{ width: '40px', height: '40px', border: '4px solid #f3f3f3', borderTop: '4px solid #3498db', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <div>Loading...</div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (redirectIfAuth && isAuthenticated && user) {
    // ✅ FIXED: graphologists were being sent to /dochome which then
    // rejected them (DoctorRoute only allows role === 'doctor'), causing
    // an infinite redirect loop. Now graphologists go to /graph-dashboard.
    let redirectPath = '/home';
    if (user.role === 'doctor') redirectPath = '/dochome';
    else if (user.role === 'graphologist') redirectPath = '/graph-dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  if (requiresAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ referrer: location.pathname }} replace />;
  }

  if (requiresAuth && roles.length > 0 && !roles.includes(user?.role)) {
    // ✅ FIXED: same redirect logic here — graphologists go to /graph-dashboard
    let redirectPath = '/home';
    if (user?.role === 'doctor') redirectPath = '/dochome';
    else if (user?.role === 'graphologist') redirectPath = '/graph-dashboard';
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

// Doctor-only Route
const DoctorRoute = ({ children }) => (
  <AuthAwareRoute requiresAuth={true} roles={['doctor']}>
    {children}
  </AuthAwareRoute>
);

// Client-only Route
const ClientRoute = ({ children }) => (
  <AuthAwareRoute requiresAuth={true} roles={['client']}>
    {children}
  </AuthAwareRoute>
);

// ✅ NEW: Graphologist-only Route
const GraphologistRoute = ({ children }) => (
  <AuthAwareRoute requiresAuth={true} roles={['graphologist']}>
    {children}
  </AuthAwareRoute>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, errorInfo) { console.error('Caught an error:', error, errorInfo); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '20px' }}>
          <h2>Something went wrong</h2>
          <p>Please try refreshing the page</p>
          <button onClick={() => window.location.reload()} style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer', borderRadius: '5px', border: '1px solid #ccc' }}>
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const StableCallHandler = React.memo(() => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  const shouldRender = React.useMemo(() => {
    if (loading || !isAuthenticated || !user) return false;
    // ✅ FIXED: added /graph-dashboard to excluded paths
    const excludedPaths = ['/login', '/signup', '/doctorLogin', '/graph-login', '/graph-dashboard', '/video-chat', '/forgot-password', '/reset-password'];
    return !excludedPaths.some(path => location.pathname.includes(path));
  }, [loading, isAuthenticated, user, location.pathname]);

  if (!shouldRender) return null;
  return <GlobalIncomingCallHandler />;
});

const AppContent = () => {
  const location = useLocation();
  const shouldHideNavbar = hideNavbarPaths.some(p => location.pathname.startsWith(p));

  return (
    <NavigationGuard>
      <StableCallHandler />
      {!shouldHideNavbar && <CustomNavbar />}

      <Routes>
        {/* Public */}
        <Route path="/" element={<HomePage />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/plans" element={<PlansPage />} />
        <Route path="/doctors" element={<DoctorsPage />} />
        <Route path="/services" element={<ServicesPage />} />
        {/* ✅ NEW: matching quiz — public, like /doctors itself */}
        <Route path="/quiz" element={<MatchingQuiz />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Auth — redirect if already logged in */}
        <Route path="/signup" element={<AuthAwareRoute redirectIfAuth={true}><SignupPage /></AuthAwareRoute>} />
        <Route path="/login" element={<AuthAwareRoute redirectIfAuth={true}><LoginPage /></AuthAwareRoute>} />
        <Route path="/doctorLogin" element={<AuthAwareRoute redirectIfAuth={true}><DocLoginPage /></AuthAwareRoute>} />
        <Route path="/graph-login" element={<AuthAwareRoute redirectIfAuth={true}><GraphLogin /></AuthAwareRoute>} />

        {/* Any authenticated user */}
        <Route path="/report-problem" element={<AuthAwareRoute requiresAuth={true}><ReportProblemPage /></AuthAwareRoute>} />
        <Route path="/profile" element={<AuthAwareRoute requiresAuth={true}><ProfilePage /></AuthAwareRoute>} />
        <Route path="/video-chat/:roomId" element={<AuthAwareRoute requiresAuth={true}><VideoChatPage /></AuthAwareRoute>} />

        {/* ✅ NEW: real messaging, any authenticated role — client, doctor, or graphologist */}
        <Route path="/messages" element={<AuthAwareRoute requiresAuth={true}><Messages /></AuthAwareRoute>} />
        {/* ✅ FIXED: /liveChat was a disconnected mock page with fake doctors
            and a hardcoded localhost socket URL. Redirected rather than left
            reachable, since it's still linked from old bookmarks/history. */}
        <Route path="/liveChat" element={<Navigate to="/messages" replace />} />

        {/* Client only */}
        <Route path="/bookings/:doctorId" element={<ClientRoute><BookingPage /></ClientRoute>} />
        <Route path="/booking/graphologist/:graphId" element={<ClientRoute><GraphBookingPage /></ClientRoute>} />

        {/* Doctor only */}
        <Route path="/dochome" element={<DoctorRoute><DocHomePage /></DoctorRoute>} />
        <Route path="/doctordashboard" element={<DoctorRoute><DocHomePage /></DoctorRoute>} />

        {/* ✅ NEW: Graphologist only */}
        <Route path="/graph-dashboard" element={<GraphologistRoute><GraphDashboard /></GraphologistRoute>} />

      {/*bookings and cencelllation*/}
      <Route path="/my-bookings" element={<ClientRoute><MyBookings /></ClientRoute>} />
      {/* ✅ NEW: previously a dead link from /my-bookings ("Handwriting Analysis →") */}
      <Route path="/my-bookings/analysis" element={<ClientRoute><MyGraphBookings /></ClientRoute>} />

      {/* ✅ NEW: internal admin tool, gated by its own secret prompt — deliberately not linked anywhere */}
      <Route path="/admin/doctors" element={<AdminDoctors />} />

        {/* 404 */}
        <Route path="*" element={
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh', flexDirection: 'column', gap: '20px' }}>
            <h2>404 - Page Not Found</h2>
            <p>The page you're looking for doesn't exist.</p>
            <button onClick={() => window.history.back()} style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer', borderRadius: '5px', border: '1px solid #ccc' }}>Go Back</button>
          </div>
        } />
      </Routes>

      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
    </NavigationGuard>
  );
};

const App = () => (
  <ErrorBoundary>
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  </ErrorBoundary>
);

export default App;